/**
 * System name display utilities
 * Converts system slugs to proper display names
 */

// Common short slugs used in games that need to map to full system names.
// Preferred display form is the LONG form ("PlayStation 4", not "PS4"),
// so short forms map onto long forms and dedup collapses them onto a
// single "Playable on:" row entry.
const COMMON_SLUG_MAPPINGS = {
  // Xbox
  'xbox-series': 'Xbox Series X|S',
  'xbox-series-x': 'Xbox Series X|S',
  'xbox-series-s': 'Xbox Series X|S',
  'xbox series x': 'Xbox Series X|S',
  'xbox series s': 'Xbox Series X|S',
  'xbox series x|s': 'Xbox Series X|S',
  'xbox-one': 'Xbox One',
  'xbox one': 'Xbox One',
  'xbox-360': 'Xbox 360',
  'xbox 360': 'Xbox 360',
  'xbox-pc': 'Windows 10',
  'windows-10': 'Windows 10',
  'windows-8': 'Windows 8',
  // PlayStation — canonical display is the long "PlayStation N" form.
  // Every short/slug variant collapses onto it so the "Playable on:" row
  // doesn't duplicate "PlayStation 4, PS4" when the DB stores both.
  'ps5': 'PlayStation 5',
  'ps4': 'PlayStation 4',
  'ps3': 'PlayStation 3',
  'ps2': 'PlayStation 2',
  'ps1': 'PlayStation 1',
  'ps': 'PlayStation',
  'playstation-5': 'PlayStation 5',
  'playstation 5': 'PlayStation 5',
  'playstation-4': 'PlayStation 4',
  'playstation 4': 'PlayStation 4',
  'playstation-3': 'PlayStation 3',
  'playstation 3': 'PlayStation 3',
  'playstation-2': 'PlayStation 2',
  'playstation 2': 'PlayStation 2',
  'playstation-1': 'PlayStation 1',
  'playstation 1': 'PlayStation 1',
  'playstation': 'PlayStation',
  'ps-vita': 'PlayStation Vita',
  'playstation-vita': 'PlayStation Vita',
  'playstation vita': 'PlayStation Vita',
  'psvita': 'PlayStation Vita',
  'ps vita': 'PlayStation Vita',
  'psp': 'PlayStation Portable',
  'playstation-portable': 'PlayStation Portable',
  'playstation portable': 'PlayStation Portable',
  // Steam/PC
  'steam-windows': 'Windows',
  'steam-mac': 'macOS',
  'steam-linux': 'Linux',
  'steam-pc': 'PC',
  'pc': 'PC',
  // Epic
  'epic-pc': 'PC',
  'epic-mac': 'macOS',
};

// Build a lookup map from systems data returned by the API
export const buildSystemLookup = (systemsData) => {
  // NOTE: we intentionally overlay COMMON_SLUG_MAPPINGS *twice* — once as the
  // seed and again at the very end. The first pass gives the API data a set
  // of defaults for any key it doesn't provide; the second pass guarantees
  // our canonical display names (e.g. `ps4 → "PlayStation 4"`) always win
  // over whatever short/legacy label the DB happens to store, so the UI
  // doesn't "blink" back to short forms once the async /systems fetch
  // completes.
  const lookup = { ...COMMON_SLUG_MAPPINGS };
  
  if (!systemsData || typeof systemsData !== 'object') {
    return lookup;
  }
  
  for (const [platformId, systems] of Object.entries(systemsData)) {
    if (!Array.isArray(systems)) continue;
    
    for (const sys of systems) {
      if (!sys.id || !sys.name) continue;
      
      // Store by full ID (e.g., "xbox-xbox-series-xs")
      lookup[sys.id] = sys.name;
      lookup[sys.id.toLowerCase()] = sys.name;
      
      // Extract parts and create multiple variations
      const parts = sys.id.split('-');
      
      if (parts.length >= 2) {
        // Without platform prefix (e.g., "xbox-series-xs" from "xbox-xbox-series-xs")
        const withoutPrefix = parts.slice(1).join('-');
        lookup[withoutPrefix] = sys.name;
        
        // Platform + second part (e.g., "xbox-series" from "xbox-xbox-series-xs")
        if (parts.length >= 3) {
          const platformPlusSecond = parts[0] + '-' + parts[2];
          lookup[platformPlusSecond] = sys.name;
          
          // Just second and third parts (e.g., "xbox-series" from "xbox-xbox-series-xs")
          // when first two parts are the same (platform repeated)
          if (parts[0] === parts[1]) {
            lookup[parts[1] + '-' + parts[2]] = sys.name;
          }
        }
        
        // For short IDs like "xbox-xbox-one", store "xbox-one"
        if (parts.length === 3 && parts[0] === parts[1]) {
          lookup[parts[0] + '-' + parts[2]] = sys.name;
        }
      }
    }
  }

  // Final overlay: canonical mappings must win over the API-supplied labels
  // (see NOTE above).
  Object.assign(lookup, COMMON_SLUG_MAPPINGS);

  return lookup;
};

// Get display name for a system slug
export const getSystemName = (slug, systemsLookup) => {
  if (!slug) return '';

  const lowerSlug = slug.toLowerCase();

  // Try direct lookup first
  if (systemsLookup?.[slug]) {
    return systemsLookup[slug];
  }

  // Try lowercase
  if (systemsLookup?.[lowerSlug]) {
    return systemsLookup[lowerSlug];
  }

  // Check common mappings directly
  if (COMMON_SLUG_MAPPINGS[lowerSlug]) {
    return COMMON_SLUG_MAPPINGS[lowerSlug];
  }

  // If the input already looks like a display name (contains a space, or is
  // already one of the known display values), return it unchanged. This avoids
  // the fallback heuristics below mangling values like "Windows 10" → "Windows"
  // or "PC" → "Windows" when the DB happens to store display names instead of
  // slugs. We treat anything that looks like a slug (lowercase + dashes) as
  // needing the heuristic mapping.
  const looksLikeSlug = /^[a-z0-9._-]+$/.test(slug);
  const knownDisplayValues = new Set(Object.values(COMMON_SLUG_MAPPINGS));
  if (!looksLikeSlug || knownDisplayValues.has(slug)) {
    return slug;
  }

  // Fallback: format slug nicely (capitalize each word, handle special cases)
  console.warn(`System slug "${slug}" not found in lookup - using formatted fallback`);

  // Handle known slug patterns. Order matters — more specific patterns first
  // so e.g. "windows-10" doesn't get caught by the bare "windows" check.
  if (lowerSlug.includes('xbox') && lowerSlug.includes('series')) return 'Xbox Series X|S';
  if (lowerSlug.includes('xbox') && lowerSlug.includes('one')) return 'Xbox One';
  if (lowerSlug.includes('xbox') && lowerSlug.includes('360')) return 'Xbox 360';
  if (lowerSlug === 'windows-10' || lowerSlug.includes('windows-10')) return 'Windows 10';
  if (lowerSlug === 'windows-8' || lowerSlug.includes('windows-8')) return 'Windows 8';
  if (lowerSlug === 'windows-11' || lowerSlug.includes('windows-11')) return 'Windows 11';
  if (lowerSlug === 'pc' || lowerSlug === 'steam-pc' || lowerSlug === 'epic-pc') return 'PC';
  if (lowerSlug.includes('windows')) return 'Windows';
  if (lowerSlug === 'ps5' || lowerSlug.includes('ps5')) return 'PS5';
  if (lowerSlug === 'ps4' || lowerSlug.includes('ps4')) return 'PS4';
  if (lowerSlug === 'ps3' || lowerSlug.includes('ps3')) return 'PS3';

  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

// Hook-style helper for use in components
export const createSystemNameGetter = (systemsData) => {
  const lookup = buildSystemLookup(systemsData);
  return (slug) => getSystemName(slug, lookup);
};
